import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { isDevFullMenu } from '@/utils/app-config'

/** 本次入口使用既有三元组规则，不改变全局权限模型。 */
export const RATE_ENTRY_MODULE = {
  plaza: 'rate-plaza', share: 'rate-share', subscribe: 'rate-subscribe',
  query: 'rate-query', maintenance: 'rate-maintenance', supplier: 'supplier-quotation-config'
} as const
export type RateEntry = keyof typeof RATE_ENTRY_MODULE
export type RateScene = 'query' | 'maintenance'
const TAB_SUFFIX: Record<string, string> = {
  ecommerce: 'ecommerce', fcl: 'fcl', preport: 'pre-port', lastmile: 'last-mile',
  lcl: 'lcl', air: 'air', express: 'express', rail: 'rail'
}

/** 租户菜单开通和角色授权同时满足；管理员仍受租户开通范围限制。 */
export function hasRateModuleAccess(entry: RateEntry): boolean {
  if (isDevFullMenu()) return true
  const auth = useAuthStore()
  const modules = entry === 'supplier'
    ? ['system-settings', RATE_ENTRY_MODULE[entry]]
    : ['super-freight', ...(['share', 'subscribe'].includes(entry) ? ['rate-plaza'] : []), RATE_ENTRY_MODULE[entry]]
  return modules.every(module => {
    const code = `module:${module}:${module}`
    return auth.hasExplicitMenuPermission(code) && auth.hasPermission(code)
  })
}

/** 页签权限只负责准入，不改变源运价的数据范围。 */
export function hasRateTabAccess(scene: RateScene, type: string): boolean {
  if (!hasRateModuleAccess(scene)) return false
  if (isDevFullMenu()) return true
  const suffix = TAB_SUFFIX[type]
  if (!suffix) return false
  const point = `${RATE_ENTRY_MODULE[scene]}-${suffix}`
  return useAuthStore().hasPermission(`tab:${point}:${point}`)
}

/** 分享使用源维护权限；滚装和散杂仅保留模块下的历史记录入口。 */
export function hasRateSourceAccess(type: string): boolean {
  return ['roro', 'bulk'].includes(type) ? hasRateModuleAccess('maintenance') : hasRateTabAccess('maintenance', type)
}

/** 仅识别本次入口及其隐藏子路由，其余业务路由原样保留。 */
function requirement(path: string): { entry: RateEntry; type?: string } | undefined {
  const supplierPrefix = '/system-settings/supplier-quotation-config'
  if (path === supplierPrefix || path.startsWith(`${supplierPrefix}/`)) return { entry: 'supplier' }
  for (const entry of ['share', 'subscribe', 'query', 'maintenance'] as const) {
    const prefix = `/saas/${entry === 'maintenance' ? 'rate-management' : `rate-${entry}`}`
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      const type = path.slice(prefix.length + 1).split('/')[0]
      return { entry, type: TAB_SUFFIX[type] ? type : undefined }
    }
  }
  return undefined
}

/** 在既有租户菜单过滤后，局部补充角色准入与详情路由权限。 */
export function applyRateEntryPermissions(routes: RouteRecordRaw[], parentPath = ''): RouteRecordRaw[] {
  return routes.flatMap(route => {
    const path = route.path.startsWith('/') ? route.path : `${parentPath}/${route.path}`.replace(/\/$/, '')
    const scope = requirement(path)
    if (scope && !hasRateModuleAccess(scope.entry)) return []
    if (scope && (scope.entry === 'query' || scope.entry === 'maintenance')) {
      const scene = scope.entry
      const types = scene === 'query' ? Object.keys(TAB_SUFFIX) : Object.keys(TAB_SUFFIX).filter(type => type !== 'ecommerce')
      if (scope.type ? !hasRateTabAccess(scene, scope.type) : !types.some(type => hasRateTabAccess(scene, type))) return []
    }
    if (route.name === 'SaasRatePlaza' && !hasRateModuleAccess('plaza')) return []
    const children = route.children ? applyRateEntryPermissions(route.children, path) : undefined
    if (route.name === 'SaasRatePlaza' && !children?.length) return []
    return [{ ...route, ...(children ? { children } : {}) } as RouteRecordRaw]
  })
}
